/**
 * Copyright (C) 2022 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React from 'react';
import { connect } from 'react-redux';
import { RootState } from '../../../../../store';
import { Draggable } from 'react-beautiful-dnd';
import DragIndicator from '@mui/icons-material/DragIndicator';
import ChartStyledWrapper from './ChartStyledWrapper';
import { Box } from '@mui/material';
import {
  CHART_WRAPPER_HEIGHT_PX,
  CHART_WRAPPER_SMALL_HEIGHT_PX,
  CHART_WRAPPER_LARGE_HEIGHT_PX,
  CHART_HEIGHT,
} from '../ChartsContainerWrapper';
import { ChartItemKeys } from '../ChartSeriesItems';
import { dateToUTCTime } from '../../../../../utils/DateTimeUtils';

interface ChartWrapperProps {
  name: string;
  id: string;
  index: number;
  children?: React.ReactNode;
  chartType?: ChartItemKeys;
  height?: CHART_HEIGHT;
}

class ChartWrapper extends React.Component<ChartWrapperProps & StateProps> {
  public render() {
    const { name, id, index, children, chartType, height, chartsLastUpdated } = this.props;

    const timestamp = chartsLastUpdated[chartType];
    let draggableMinHeight = CHART_WRAPPER_HEIGHT_PX;
    let chartPaddingTop = '0px';
    let chartMinHeight = CHART_WRAPPER_HEIGHT_PX - 34;

    switch (height) {
      case CHART_HEIGHT.SMALL:
        draggableMinHeight = CHART_WRAPPER_SMALL_HEIGHT_PX;
        chartPaddingTop = '5px';
        chartMinHeight = CHART_WRAPPER_SMALL_HEIGHT_PX - 40;
        break;

      case CHART_HEIGHT.LARGE:
        draggableMinHeight = CHART_WRAPPER_LARGE_HEIGHT_PX;
        chartPaddingTop = '0px';
        chartMinHeight = CHART_WRAPPER_LARGE_HEIGHT_PX - 34;
        break;

      default:
        break; // Do nothing, CHART_HEIGHT.DEFAULT
    }

    return (
      <Draggable key={id} draggableId={id} index={index}>
        {(provided: any, snapshot: any) => (
          <Box ref={provided.innerRef} {...provided.draggableProps} minHeight={draggableMinHeight} paddingBottom="8px">
            <ChartStyledWrapper data-test={`ftv__charts__${id}`}>
              <div className={'ftv__charts__header'}>
                <span className="ftv__charts__title">{name}</span>
                {chartType && timestamp > 0 ? (
                  <span className="ftv__charts__time">{` Last updated ${dateToUTCTime(
                    new Date(timestamp * 1000),
                    'HH:mm:ss',
                  )}`}</span>
                ) : null}
                <span {...provided.dragHandleProps} className="ftv__charts__draggable--dragIndicator">
                  <DragIndicator fontSize="small" />
                </span>
              </div>
              <Box width="100%" paddingTop={chartPaddingTop} minHeight={chartMinHeight}>
                {children}
              </Box>
            </ChartStyledWrapper>
          </Box>
        )}
      </Draggable>
    );
  }
}

const mapStateToProps = (state: RootState, ownProps: ChartWrapperProps): any => ({
  chartsLastUpdated: state.terminalHistory.chartsLastUpdated,
});
type StateProps = ReturnType<typeof mapStateToProps>;

export default connect(mapStateToProps, null)(ChartWrapper);
